/**
 * Progresión del programa.
 *
 * Funciones PURAS. Reciben las sesiones y devuelven el estado; no leen de
 * ningún sitio y ningún componente decide política.
 *
 * CAMBIO APROBADO #5: `semanaActual` y `baselineCompletado` se DERIVAN, no se
 * almacenan. Guardarlos garantiza que algún día se desincronicen de la realidad
 * (el contador dice semana 5 y hay 12 sesiones hechas).
 */

import { cuentaComoHecha, estaEnProgreso, type SesionRegistrada } from '../modelo/sesion.js';

/** Las 7 sesiones-día del ciclo, en orden. */
export const CLAVES_DIA_ORDENADAS = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7'] as const;

/** Los checkpoints, en orden. */
export const SEMANAS_CHECKPOINT = [4, 8, 12] as const;

/**
 * El baseline se hizo si existe una sesión de baseline COMPLETADA.
 *
 * `parcial` NO cuenta. El baseline decide el peso de las doce semanas: si se
 * omitió la muestra de audio o el listening, arrancar el Día 1 sobre eso sería
 * darlo por hecho artificialmente. La interfaz ofrece rehacerlo
 * (`baselineIncompleto` en `baseline.ts`).
 */
export function baselineCompletado(sesiones: SesionRegistrada[]): boolean {
  return sesiones.some(
    (s) => s.tipo === 'baseline' && !s.deleted && s.estado === 'completada',
  );
}

/**
 * Semana actual = ciclos de 7 sesiones-día completados + 1.
 *
 * Los días son SECUENCIALES, no de calendario: si se pierde un día, se continúa
 * con la siguiente sesión pendiente. La semana avanza al terminar las 7, no
 * cuando pasan siete días.
 */
export function semanaActual(sesiones: SesionRegistrada[]): number {
  const dias = sesiones.filter((s) => s.tipo === 'dia' && cuentaComoHecha(s));
  return Math.floor(dias.length / CLAVES_DIA_ORDENADAS.length) + 1;
}

/** Qué sesiones-día del ciclo en curso ya están hechas. */
export function diasHechosDelCiclo(sesiones: SesionRegistrada[]): Set<string> {
  const semana = semanaActual(sesiones);
  return new Set(
    sesiones
      .filter((s) => s.tipo === 'dia' && cuentaComoHecha(s) && s.semana === semana)
      .map((s) => s.sessionKey),
  );
}

/**
 * Checkpoint pendiente, o `null`.
 *
 * Se debe al COMPLETAR el ciclo 4, 8 o 12, antes de empezar el siguiente.
 * RECOMIENDA; no bloquea (CA-7.2).
 */
export function checkpointPendiente(sesiones: SesionRegistrada[]): number | null {
  if (!baselineCompletado(sesiones)) return null;

  const ciclosCompletos = semanaActual(sesiones) - 1;
  const hechos = new Set(
    sesiones
      .filter((s) => s.tipo === 'checkpoint' && cuentaComoHecha(s))
      .map((s) => s.checkpointSemana),
  );

  for (const semana of SEMANAS_CHECKPOINT) {
    if (ciclosCompletos >= semana && !hechos.has(semana)) return semana;
  }
  return null;
}

/** La sesión abierta que se puede reanudar, si existe. */
export function sesionEnProgreso(sesiones: SesionRegistrada[]): SesionRegistrada | null {
  const abiertas = sesiones.filter(estaEnProgreso);
  if (abiertas.length === 0) return null;
  // Si por algún motivo hubiera varias, la más reciente es la relevante.
  return abiertas.reduce((a, b) => (a.iniciadaEn >= b.iniciadaEn ? a : b));
}

export type Recomendacion =
  | { tipo: 'reanudar'; sesion: SesionRegistrada }
  | { tipo: 'baseline' }
  | { tipo: 'checkpoint'; semana: number }
  | { tipo: 'dia'; sessionKey: string; semana: number };

/**
 * Qué toca ahora.
 *
 * Orden de prioridad:
 *   1. Reanudar una sesión abierta.
 *   2. El baseline, si no está hecho.
 *   3. Un checkpoint pendiente (recomendado, no obligatorio).
 *   4. La siguiente sesión-día del ciclo.
 */
export function siguienteRecomendada(sesiones: SesionRegistrada[]): Recomendacion {
  const abierta = sesionEnProgreso(sesiones);
  if (abierta) return { tipo: 'reanudar', sesion: abierta };

  if (!baselineCompletado(sesiones)) return { tipo: 'baseline' };

  const checkpoint = checkpointPendiente(sesiones);
  if (checkpoint !== null) return { tipo: 'checkpoint', semana: checkpoint };

  const semana = semanaActual(sesiones);
  const hechos = diasHechosDelCiclo(sesiones);
  const siguiente = CLAVES_DIA_ORDENADAS.find((clave) => !hechos.has(clave));

  // `find` siempre encuentra uno: si estuvieran los 7, `semanaActual` ya habría
  // avanzado y `hechos` estaría vacío para el ciclo nuevo.
  return { tipo: 'dia', sessionKey: siguiente ?? 'd1', semana };
}

/**
 * Una reanudación tras una pausa larguísima probablemente no es una
 * reanudación: el usuario se fue y volvió otro día (CA-3.13).
 *
 * Umbral: el doble del tiempo planificado. En vez de acumular horas irreales en
 * `segundosReales`, la aplicación ofrece cerrarla como `parcial`.
 */
export function pausaDemasiadoLarga(
  sesion: SesionRegistrada,
  ahora: Date = new Date(),
): boolean {
  const paso = sesion.pasos[sesion.pasoActual];
  const referencia = paso?.iniciadoEn ?? sesion.iniciadaEn;
  const transcurridoMs = ahora.getTime() - new Date(referencia).getTime();
  return transcurridoMs > sesion.duracionPlanificada * 60 * 1000 * 2;
}

/*
 * El temporizador vive en `sesionActiva.ts`, no aquí.
 *
 * Tener dos implementaciones del mismo cálculo es la forma más segura de que
 * una de las dos se quede atrás: `sesionActiva` suma segmentos y soporta
 * pausa, que es lo que la sesión necesita de verdad.
 */
